import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import useStore from 'services/store';
import { useApi } from 'services/api';
import { extractErrorMessage } from 'utils/api';
import { transformStringToKey } from 'utils/pql';
import { getConfiguredTimezone } from 'utils/datetime';

const transformKey = transformStringToKey;

const useSearchAlternated = ({
  pqlQuery,
  pqlAlternation,
  searchType,
  context,
  registryGroupKey, // optional key for grouping searches
  skipRegistry = false, // optional skip registering search
  transformResponse, // optional transform fn
  onSearchSuccess, // optional onSuccess hook
  onSearchFail, // optional onFail hook
}) => {
  const api = useApi();
  const { activeSensorsByProfile, registerComponent, deregisterComponent } = useStore((state) => ({
    activeSensorsByProfile: state.searchQuery.activeSensorsByProfile,
    registerComponent: state.searchRegistry.registerComponent,
    deregisterComponent: state.searchRegistry.deregisterComponent,
  }));
  const timezone = getConfiguredTimezone();
  const isEnabled = !!pqlQuery && !!pqlAlternation && activeSensorsByProfile !== undefined;

  const pqlQueryAlternated = `${pqlQuery || ''} ${pqlAlternation || ''}`;
  const groupKey = registryGroupKey || transformKey(window.location.pathname);

  const opts = useMemo(
    () => ({
      context: context || activeSensorsByProfile || [],
      searchType,
    }),
    [context, activeSensorsByProfile, searchType],
  );

  const { isLoading, data, status, error, fetchStatus, dataUpdatedAt, errorUpdatedAt, ...rest } = useQuery({
    queryKey: [groupKey, transformKey(pqlQuery), transformKey(pqlAlternation), opts],
    queryFn: ({ signal }) =>
      api.get('/search/query', {
        params: { query: pqlQueryAlternated, timezone, ...opts },
        signal,
      }),
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes before garbage collection
  });

  useEffect(() => {
    if (dataUpdatedAt) onSearchSuccess?.(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callback identity is unstable; only fire on new fetch completion
  }, [dataUpdatedAt]);

  useEffect(() => {
    if (errorUpdatedAt) onSearchFail?.(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callback identity is unstable; only fire on new error
  }, [errorUpdatedAt]);

  useEffect(() => {
    if (fetchStatus === 'idle') deregisterComponent(groupKey, pqlQueryAlternated);
  }, [fetchStatus, deregisterComponent, groupKey, pqlQueryAlternated]);

  useEffect(() => {
    return () => {
      !skipRegistry && deregisterComponent(groupKey, pqlQueryAlternated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKey, pqlQueryAlternated]);

  useEffect(() => {
    if (isEnabled && status === 'pending' && !skipRegistry) {
      registerComponent(groupKey, pqlQueryAlternated);
    }
  }, [isEnabled, status, skipRegistry, registerComponent, groupKey, pqlQueryAlternated]);

  const processedResults = useMemo(
    () => (transformResponse && data ? transformResponse(data?.results) : data?.results),
    [data, transformResponse],
  );

  return {
    loading: isLoading,
    results: processedResults,
    compiled: data?.compiled,
    timerange: data?.compiled?.daterange,
    error: error ? extractErrorMessage(error) : null,
    ...rest,
  };
};

export default useSearchAlternated;
